// src/pages/admin/FeaturesManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const FeaturesManager = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'fas fa-check',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchFeatures();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      icon: 'fas fa-check',
      display_order: 0,
      is_active: true
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/features.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedFeatures = response.data.data.map(feature => ({
          ...feature,
          id: parseInt(feature.id),
          display_order: parseInt(feature.display_order),
          is_active: feature.is_active === "1" || feature.is_active === 1,
          created_at: feature.created_at
        })).sort((a, b) => {
          if (a.display_order === b.display_order) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return a.display_order - b.display_order;
        });
        
        setFeatures(processedFeatures);
      } else {
        setError('Failed to load features');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      const dataToSend = {
        title: formData.title,
        description: formData.description,
        icon: formData.icon,
        display_order: formData.display_order,
        is_active: formData.is_active ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      
      if (editingFeature) {
        response = await axios.put(
          `${API_BASE_URL}/features.php?id=${editingFeature.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/features.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingFeature ? 'Feature updated!' : 'Feature added!');
        setShowModal(false);
        resetForm();
        setEditingFeature(null);
        fetchFeatures();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving feature: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this feature?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/features.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Feature deleted!');
          fetchFeatures();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete feature: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting feature');
        console.error(err);
      }
    }
  };

  const handleEdit = (feature) => {
    setEditingFeature(feature);
    setFormData({
      title: feature.title,
      description: feature.description || '',
      icon: feature.icon || 'fas fa-check',
      display_order: parseInt(feature.display_order),
      is_active: feature.is_active === true || feature.is_active === "1" || feature.is_active === 1
    });
    setShowModal(true);
  };

  const handleReorder = async (direction, feature) => {
    try {
      const currentFeatures = [...features];
      const currentIndex = currentFeatures.findIndex(item => item.id === feature.id);
      
      const token = localStorage.getItem('token');
      
      if (direction === 'up' && currentIndex > 0) {
        const prevFeature = currentFeatures[currentIndex - 1];
        
        await axios.put(`${API_BASE_URL}/features.php?id=${feature.id}`, {
          ...feature,
          display_order: prevFeature.display_order,
          is_active: (feature.is_active === true || feature.is_active === "1" || feature.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await axios.put(`${API_BASE_URL}/features.php?id=${prevFeature.id}`, {
          ...prevFeature,
          display_order: feature.display_order,
          is_active: (prevFeature.is_active === true || prevFeature.is_active === "1" || prevFeature.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
        
      } else if (direction === 'down' && currentIndex < currentFeatures.length - 1) {
        const nextFeature = currentFeatures[currentIndex + 1];
        
        await axios.put(`${API_BASE_URL}/features.php?id=${feature.id}`, {
          ...feature,
          display_order: nextFeature.display_order,
          is_active: (feature.is_active === true || feature.is_active === "1" || feature.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await axios.put(`${API_BASE_URL}/features.php?id=${nextFeature.id}`, {
          ...nextFeature,
          display_order: feature.display_order,
          is_active: (nextFeature.is_active === true || nextFeature.is_active === "1" || nextFeature.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
      }
      
      fetchFeatures();
      
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to reorder: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleActive = async (feature) => {
    try {
      const currentActive = feature.is_active === true || feature.is_active === "1" || feature.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        title: feature.title,
        description: feature.description || '',
        icon: feature.icon || 'fas fa-check',
        display_order: parseInt(feature.display_order),
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/features.php?id=${feature.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchFeatures();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFeature(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading features...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Features Manager</h2>
            <p className="text-muted">Manage your website features section</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = features.length > 0 
                ? Math.max(...features.map(feature => parseInt(feature.display_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                title: '',
                description: '',
                icon: 'fas fa-check',
                display_order: nextOrder,
                is_active: true
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Feature
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="50">Order</th>
                  <th width="100">Icon</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => {
                  const serialNumber = index + 1;
                  
                  return (
                    <tr key={feature.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('up', feature)}
                            disabled={parseInt(feature.display_order) <= 1}
                          >
                            <i className="fas fa-arrow-up"></i>
                          </Button>
                          <span className="mx-2">{serialNumber}</span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('down', feature)}
                            disabled={parseInt(feature.display_order) >= features.length}
                          >
                            <i className="fas fa-arrow-down"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        <div className="text-center">
                          <i className={`${feature.icon} fa-2x text-primary`}></i>
                        </div>
                      </td>
                      <td>
                        <strong>{feature.title}</strong>
                      </td>
                      <td>
                        <small className="text-muted">
                          {truncateText(feature.description || '', 80)}
                        </small>
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={feature.is_active === true || feature.is_active === "1" || feature.is_active === 1}
                          onChange={() => handleToggleActive(feature)}
                          label={(feature.is_active === true || feature.is_active === "1" || feature.is_active === 1) ? 'Active' : 'Inactive'}
                          key={`active-${feature.id}-${feature.is_active}`}
                        />
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(feature.created_at)}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(feature)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(feature.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            
            {features.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-star fa-3x mb-3"></i>
                <p>No features found. Add your first feature!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Feature Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingFeature ? 'Edit Feature' : 'Add Feature'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Easy to Use, Fast Performance"
                    />
                    <Form.Text className="text-muted">
                      Feature title (displayed prominently)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleTextareaChange}
                  rows={3}
                  placeholder="Describe this feature in detail..."
                />
                <Form.Text className="text-muted">
                  Optional description (supports HTML)
                </Form.Text>
              </Form.Group>
              
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon Class</Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="text"
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                        placeholder="fas fa-check"
                      />
                      {formData.icon && (
                        <div className="ms-3">
                          <i className={`${formData.icon} fa-2x text-primary`}></i>
                        </div>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      FontAwesome classes (e.g., fas fa-check, fas fa-bolt, fas fa-shield-alt)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive features won't be shown on the website
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Common Feature Icons Suggestions */}
              <div className="mb-3">
                <Form.Label className="d-block">Common Feature Icons:</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    { icon: 'fas fa-check', label: 'Check' },
                    { icon: 'fas fa-bolt', label: 'Fast' },
                    { icon: 'fas fa-shield-alt', label: 'Secure' },
                    { icon: 'fas fa-rocket', label: 'Rocket' },
                    { icon: 'fas fa-cogs', label: 'Settings' },
                    { icon: 'fas fa-users', label: 'Users' },
                    { icon: 'fas fa-chart-line', label: 'Analytics' },
                    { icon: 'fas fa-mobile-alt', label: 'Mobile' },
                    { icon: 'fas fa-globe', label: 'Global' },
                    { icon: 'fas fa-lock', label: 'Lock' },
                    { icon: 'fas fa-headset', label: 'Support' },
                    { icon: 'fas fa-sync-alt', label: 'Sync' },
                    { icon: 'fas fa-cloud', label: 'Cloud' },
                    { icon: 'fas fa-database', label: 'Database' },
                    { icon: 'fas fa-code', label: 'Code' },
                    { icon: 'fas fa-paint-brush', label: 'Design' }
                  ].map((item, idx) => (
                    <Button
                      key={idx}
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setFormData({...formData, icon: item.icon})}
                      className="d-flex align-items-center"
                    >
                      <i className={`${item.icon} me-2`}></i>
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {editingFeature && (
                <div className="alert alert-info">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingFeature.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingFeature ? 'Update' : 'Add'} Feature
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default FeaturesManager;